import { formatCompactCurrency, overageLabel } from "./metricsFormat";
import type { PartnerMetricsRow } from "./metrics.types";
import type { HealthBand, PartnerStatus } from "./partnerStatus";

/**
 * One source of truth for Metrics columns: sorting metadata plus a complete
 * display projection and mobile placement for each field. `metricsView.ts`
 * reads the sort policy; the desktop table and mobile tiles interpret the same
 * projected cells, so formatting and status treatment cannot drift between
 * responsive adapters.
 */

type SortValueFn = (row: PartnerMetricsRow) => number | string;

interface MetricsColumnDef {
  readonly id: string;
  readonly label: string;
  /** Right-align header + cells (numbers) vs left-align (text). */
  readonly numeric: boolean;
  /** First click on this column sorts ascending (names) rather than descending. */
  readonly sortAscByDefault: boolean;
  readonly sortValue: SortValueFn;
  readonly tileSlot: "title" | "body" | "status";
  readonly project: (row: PartnerMetricsRow) => MetricsCellProjection;
}

export type MetricsCellProjection =
  | { kind: "text"; text: string; emphasis: "none" | "critical" }
  | { kind: "partner-status"; label: PartnerStatus; band: HealthBand };

const text = (
  value: string | number,
  emphasis: "none" | "critical" = "none"
): MetricsCellProjection => ({ kind: "text", text: String(value), emphasis });

export const METRICS_COLUMNS = [
  {
    id: "name",
    label: "Partner",
    numeric: false,
    tileSlot: "title",
    sortAscByDefault: true,
    sortValue: (row: PartnerMetricsRow) => row.partner.name.toLowerCase(),
    project: (row: PartnerMetricsRow) => text(row.partner.name),
  },
  {
    id: "tier",
    label: "Tier",
    numeric: false,
    tileSlot: "body",
    sortAscByDefault: true,
    sortValue: (row: PartnerMetricsRow) => row.tier.name.toLowerCase(),
    project: (row: PartnerMetricsRow) => text(row.tier.name),
  },
  {
    id: "acv",
    label: "ACV",
    numeric: true,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.partner.acv,
    project: (row: PartnerMetricsRow) => text(formatCompactCurrency(row.partner.acv)),
  },
  {
    id: "savedPct",
    label: "Saved %",
    numeric: true,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.utilization.savedPct,
    project: (row: PartnerMetricsRow) => text(`${row.utilization.savedPct}%`),
  },
  {
    id: "adhocPct",
    label: "Ad Hoc %",
    numeric: true,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.utilization.adhocPct,
    project: (row: PartnerMetricsRow) => text(`${row.utilization.adhocPct}%`),
  },
  {
    id: "growth",
    label: "7d Growth",
    numeric: true,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.growth.pct,
    project: (row: PartnerMetricsRow) => text(row.growth.label),
  },
  {
    id: "apiCount",
    label: "APIs",
    numeric: false,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.apiBreadth.length,
    project: (row: PartnerMetricsRow) => text(row.apiBreadth.join(", ")),
  },
  {
    id: "drift",
    label: "Drift",
    numeric: true,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.driftCount,
    project: (row: PartnerMetricsRow) => text(row.driftCount),
  },
  {
    id: "errorRate",
    label: "Error %",
    numeric: true,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.errorPct,
    project: (row: PartnerMetricsRow) => text(`${row.errorPct}%`),
  },
  {
    id: "overage",
    label: "Overage",
    numeric: true,
    tileSlot: "body",
    sortAscByDefault: false,
    sortValue: (row: PartnerMetricsRow) => row.overage.total,
    project: (row: PartnerMetricsRow) =>
      text(overageLabel(row.overage), row.overage.isBreached ? "critical" : "none"),
  },
  {
    id: "status",
    label: "Status",
    numeric: false,
    tileSlot: "status",
    sortAscByDefault: true,
    sortValue: (row: PartnerMetricsRow) => row.status.toLowerCase(),
    project: (row: PartnerMetricsRow) => ({
      kind: "partner-status",
      label: row.status,
      band: row.band,
    }),
  },
] as const satisfies readonly MetricsColumnDef[];

export type MetricsColumn = (typeof METRICS_COLUMNS)[number];
export type MetricsSortColumn = MetricsColumn["id"];

export interface MetricsSort {
  column: MetricsSortColumn;
  direction: "asc" | "desc";
}

const COLUMN_BY_ID = Object.fromEntries(
  METRICS_COLUMNS.map((column) => [column.id, column])
) as Record<MetricsSortColumn, MetricsColumn>;

export function metricsColumn(id: MetricsSortColumn): MetricsColumn {
  return COLUMN_BY_ID[id];
}
